import React, { useState, useEffect } from 'react'
import Modal from '../../../../common/Modal'
import { Button } from '../../../../common/index'

interface SalesEditModalProps {
  isOpen: boolean
  closeModal: () => void
  onSave?: (directSales: number, resellerSales: number) => void
  initialDirectSales?: number
  initialResellerSales?: number
  maxTotalGeneratedCompletedOrders?: number
}

const SalesEditModal: React.FC<SalesEditModalProps> = ({
  isOpen,
  closeModal,
  onSave,
  initialDirectSales = 0,
  initialResellerSales = 0,
  maxTotalGeneratedCompletedOrders = 0
}) => {
  const [directSales, setDirectSales] = useState(initialDirectSales);
  const [resellerSales, setResellerSales] = useState(initialResellerSales);

  // Uppdatera sliders när initialvärden ändras (t.ex. vid byte av häfte)
  useEffect(() => {
    setDirectSales(initialDirectSales);
    setResellerSales(initialResellerSales);
  }, [initialDirectSales, initialResellerSales]);

  // Validering för sliders - låt den som dras behålla sitt värde, justera den andra
  const validateSliderChange = (newDirectSales: number, newResellerSales: number, isDirectSalesChanging: boolean) => {
    const total = newDirectSales + newResellerSales;
    if (total > maxTotalGeneratedCompletedOrders) {
      if (isDirectSalesChanging) {
        // Om direktförsäljning ändras, begränsa återförsäljning
        return {
          directSales: newDirectSales,
          resellerSales: Math.max(0, maxTotalGeneratedCompletedOrders - newDirectSales)
        };
      } else {
        // Om återförsäljning ändras, begränsa direktförsäljning
        return {
          directSales: Math.max(0, maxTotalGeneratedCompletedOrders - newResellerSales),
          resellerSales: newResellerSales
        };
      }
    }
    return { directSales: newDirectSales, resellerSales: newResellerSales };
  };

  // Validering för inputs - låt det fält som ändrades behålla sitt värde, justera det andra
  const validateInputBlur = (newDirectSales: number, newResellerSales: number, isDirectSalesChanging: boolean) => {
    const total = newDirectSales + newResellerSales;
    if (total > maxTotalGeneratedCompletedOrders) {
      if (isDirectSalesChanging) {
        // Om direktförsäljning ändrades, begränsa återförsäljning
        return {
          directSales: newDirectSales,
          resellerSales: Math.max(0, maxTotalGeneratedCompletedOrders - newDirectSales)
        };
      } else {
        // Om återförsäljning ändrades, begränsa direktförsäljning
        return {
          directSales: Math.max(0, maxTotalGeneratedCompletedOrders - newResellerSales),
          resellerSales: newResellerSales
        };
      }
    }
    return { directSales: newDirectSales, resellerSales: newResellerSales };
  };

  // Hantera förändring av direktförsäljning från slider
  const handleDirectSalesChange = (value: number) => {
    const validated = validateSliderChange(value, resellerSales, true);
    setDirectSales(validated.directSales);
    setResellerSales(validated.resellerSales);
  };

  // Hantera förändring av direktförsäljning från input
  const handleDirectSalesInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    // Tillåt tom sträng för bättre UX
    if (inputValue === '') {
      setDirectSales(0);
      return;
    }

    // Kontrollera att det bara är siffror
    if (!/^\d+$/.test(inputValue)) {
      return; // Ignorera ogiltiga tecken
    }

    const value = parseInt(inputValue);
    const clampedValue = Math.max(0, Math.min(value, maxTotalGeneratedCompletedOrders));
    setDirectSales(clampedValue);
  };

  // Validering när användaren lämnar direktförsäljnings-inputet
  const handleDirectSalesInputBlur = () => {
    // Säkerställ att vi har ett giltigt värde
    const currentValue = directSales || 0;
    const validated = validateInputBlur(currentValue, resellerSales, true);
    setDirectSales(validated.directSales);
    setResellerSales(validated.resellerSales);
  };

  // Hantera förändring av återförsäljning från slider
  const handleResellerSalesChange = (value: number) => {
    const validated = validateSliderChange(directSales, value, false);
    setDirectSales(validated.directSales);
    setResellerSales(validated.resellerSales);
  };

  // Hantera förändring av återförsäljning från input
  const handleResellerSalesInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    // Tillåt tom sträng för bättre UX
    if (inputValue === '') {
      setResellerSales(0);
      return;
    }

    // Kontrollera att det bara är siffror
    if (!/^\d+$/.test(inputValue)) {
      return; // Ignorera ogiltiga tecken
    }

    const value = parseInt(inputValue);
    const clampedValue = Math.max(0, Math.min(value, maxTotalGeneratedCompletedOrders));
    setResellerSales(clampedValue);
  };

  // Validering när användaren lämnar återförsäljnings-inputet
  const handleResellerSalesInputBlur = () => {
    // Säkerställ att vi har ett giltigt värde
    const currentValue = resellerSales || 0;
    const validated = validateInputBlur(directSales, currentValue, false);
    setDirectSales(validated.directSales);
    setResellerSales(validated.resellerSales);
  };


  const handleSave = () => {
    if (onSave) {
      onSave(directSales, resellerSales);
    }
    closeModal();
  };

  const handleCancel = () => {
    // Reset to initial values
    setDirectSales(initialDirectSales);
    setResellerSales(initialResellerSales);
    closeModal();
  };

  return (
    <Modal
      isOpen={isOpen}
      closeModal={handleCancel}
      className="modal-sales-edit"
    >
      <h2 className="modal-title">Sålda värdebevis</h2>
      <p className="modal-description">
        Drag i reglagen eller fyll i antalet direkt i vita rutan. Kom ihåg att trycka på "Spara" för att statistiken ska uppdateras.
      </p>

      <div className="slider-group">
        <label className="slider-label" htmlFor="direct-sales-slider">Värdebevis sålda direkt av er</label>
        <div className="slider-container">
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={directSales === 0 ? '' : directSales.toString()}
            onChange={handleDirectSalesInputChange}
            onBlur={handleDirectSalesInputBlur}
            className="slider-value-input"
            placeholder="0"
          />
          <input
            id="direct-sales-slider"
            type="range"
            min="0"
            max={maxTotalGeneratedCompletedOrders}
            value={directSales}
            onChange={(e) => handleDirectSalesChange(parseInt(e.target.value))}
            className="slider-input"
            style={{ '--progress': `${maxTotalGeneratedCompletedOrders > 0 ? (directSales / maxTotalGeneratedCompletedOrders) * 100 : 0}%` } as React.CSSProperties}
          />
        </div>
      </div>

      <div className="slider-group">
        <label className="slider-label" htmlFor="reseller-sales-slider">Värdebevis sålda av återförsäljare</label>
        <div className="slider-container">
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={resellerSales === 0 ? '' : resellerSales.toString()}
            onChange={handleResellerSalesInputChange}
            onBlur={handleResellerSalesInputBlur}
            className="slider-value-input"
            placeholder="0"
          />
          <input
            id="reseller-sales-slider"
            type="range"
            min="0"
            max={maxTotalGeneratedCompletedOrders}
            value={resellerSales}
            onChange={(e) => handleResellerSalesChange(parseInt(e.target.value))}
            className="slider-input"
            style={{ '--progress': `${maxTotalGeneratedCompletedOrders > 0 ? (resellerSales / maxTotalGeneratedCompletedOrders) * 100 : 0}%` } as React.CSSProperties}
          />
        </div>
      </div>

      <div className="modal-actions">
        <Button className="primary alt md" onClick={handleSave}>Spara</Button>
        <Button className="secondary md" onClick={handleCancel}>Avbryt</Button>
      </div>
    </Modal>
  )
}

export default SalesEditModal
